import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { PrivacyPolicyRender } from '../legal/privacy_policy_render';
import { TermsConditionsRender } from '../legal/terms_conditions_render';
import { ReturnPolicyRender } from '../legal/return_policy_render';

export type LegalDocumentType = 'privacy' | 'terms' | 'return';

const TITLES: Record<LegalDocumentType, string> = {
    privacy: 'Privacy Policy',
    terms: 'Terms and Conditions',
    return: 'Return Policy',
};

interface LegalDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    documentType: LegalDocumentType;
    requireAcknowledgment?: boolean;
    onAccept?: () => void;
}

export default function LegalDocumentDialog({
    open,
    onClose,
    documentType,
    requireAcknowledgment = false,
    onAccept,
}: LegalDocumentDialogProps) {
    const [acknowledged, setAcknowledged] = React.useState(false);
    const isTermsWithAck = documentType === 'terms' && requireAcknowledgment;

    React.useEffect(() => {
        if (!open) {
            setAcknowledged(false);
        }
    }, [open]);

    const handleClose = () => {
        if (isTermsWithAck) return;
        onClose();
    };

    const handleAccept = () => {
        onAccept?.();
        setAcknowledged(false);
        onClose();
    };

    const contentSx = {
        maxHeight: '70vh',
        overflow: 'auto' as const,
        pr: 1,
        backgroundColor: '#ffffff',
    };

    return (
        <Dialog
            open={open}
            onClose={isTermsWithAck ? undefined : handleClose}
            maxWidth="lg"
            fullWidth
            keepMounted
            disableEscapeKeyDown={isTermsWithAck}
            PaperProps={{
                sx: {
                    maxHeight: '90vh',
                    margin: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="div">
                    {TITLES[documentType]}
                </Typography>
                {!isTermsWithAck && (
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={contentSx}>
                    <Box sx={{ display: documentType === 'privacy' ? 'block' : 'none' }}>
                        <PrivacyPolicyRender />
                    </Box>
                    <Box sx={{ display: documentType === 'terms' ? 'block' : 'none' }}>
                        <TermsConditionsRender />
                    </Box>
                    <Box sx={{ display: documentType === 'return' ? 'block' : 'none' }}>
                        <ReturnPolicyRender />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                    flexDirection: isTermsWithAck ? 'column' : 'row',
                    alignItems: isTermsWithAck ? 'stretch' : 'center',
                }}
            >
                {isTermsWithAck ? (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acknowledged}
                                    onChange={(e) => setAcknowledged(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    I have read and agree to the Terms and Conditions
                                </Typography>
                            }
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={onClose} variant="outlined" disabled={false}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAccept}
                                variant="contained"
                                color="primary"
                                disabled={!acknowledged}
                            >
                                I Agree
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Button onClick={handleClose} variant="contained" color="primary">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
